import { useEffect, useRef, useState } from 'react';
import type { CardInfo } from '@lub0v/santa-utils';

export interface ParticipantPage {
  items: CardInfo[];
  related: CardInfo[];
  total: number;
  page: number;
  pageSize: number;
  summary: { total: number; named: number; unassigned: number; exclusions: number; invited: number };
}

export function useOrganizerParticipantPage(key: string, enabled: boolean, request: {
  page: number; pageSize: number; search: string; filters: string; sort: string;
}, revision: unknown) {
  const [result, setResult] = useState<{ key: string; data: ParticipantPage } | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const cache = useRef(new Map<number, CardInfo>());
  const cacheKey = useRef({ key, revision });
  if (cacheKey.current.key !== key || cacheKey.current.revision !== revision) {
    cache.current.clear(); cacheKey.current = { key, revision };
  }
  const query = new URLSearchParams(Object.entries(request).map(([name, value]) => [name, String(value)])).toString();
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    // Debounce search changes and cancel superseded requests (including page-size changes).
    const timer = window.setTimeout(() => {
      void fetch(`/api/box/${encodeURIComponent(key)}/participants?${query}`, { signal: controller.signal })
        .then(async response => {
          if (!response.ok) throw new Error('Participant page unavailable');
          const data: ParticipantPage = await response.json();
          if (controller.signal.aborted) return;
          for (const card of [...data.items, ...data.related]) cache.current.set(card.id, card);
          setResult({ key, data });
          setLoading(false);
        }).catch(() => { if (!controller.signal.aborted) { setError(true); setLoading(false); } });
    }, 150);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [key, enabled, query, revision]);
  return { data: result?.key === key ? result.data : null, error, loading, cachedCards: [...cache.current.values()] };
}
